import { Flex, Spinner, Text } from "@chakra-ui/react";
import { format } from "date-fns";
import CustomIcon from "../../../public/icons-v1_1";
import AppButton from "@/features/line-sheet-sets/app-button";
import StatusBadge from "@/shared/components/StatusBadge";

export type BusinessRegistrationCertificateStatus =
  // 등록 여부를 조회하는 중
  | "LOADING"
  // 등록된 사업자등록증이 없음
  | "EMPTY"
  // 방금 고른 파일을 자동 인식(OCR)하는 중
  | "PARSING"
  // 자동 인식은 끝났지만 아직 저장 버튼을 누르지 않음
  | "UNSAVED"
  // 서버에 등록된 상태
  | "SAVED";

interface BusinessRegistrationCertificateRowProps {
  status: BusinessRegistrationCertificateStatus;
  // 등록된(또는 인식 중인) 파일명. EMPTY / LOADING 이면 없다.
  fileName: string | null;
  registeredAt: Date | null;
  onViewFile: () => void;
}

export default function BusinessRegistrationCertificateRow({
  status,
  fileName,
  registeredAt,
  onViewFile,
}: BusinessRegistrationCertificateRowProps) {
  // 아직 파일이 없는 동안은 아이콘·배지 없이 안내 문구만 둔다.
  if (status === "LOADING" || status === "EMPTY") {
    return (
      <Flex flexDirection={"column"} gap={"4px"}>
        <Flex flexDirection={"row"} alignItems={"center"} gap={"8px"}>
          <Text textStyle={"body-md-bold"} color={"#1D1D1B"}>
            {status === "LOADING"
              ? "사업자등록증을 불러오는 중입니다"
              : "등록된 사업자등록증이 없습니다."}
          </Text>
          {status === "LOADING" && <Spinner size={"sm"} color={"#8F8F8C"} />}
        </Flex>
        {status === "EMPTY" && (
          <Text textStyle={"body-sm-regular"} color={"#8F8F8C"}>
            등록하면 사업자등록번호와 사업자명을 자동으로 확인합니다.
          </Text>
        )}
      </Flex>
    );
  }

  // 저장 전 파일도 사용자가 확인할 수 있어야 한다.
  const isViewable = status === "SAVED" || status === "UNSAVED";

  return (
    <Flex flexDirection={"row"} alignItems={"center"} gap={"12px"}>
      <Flex
        flexShrink={"0"}
        width={"32px"}
        height={"32px"}
        borderRadius={"4px"}
        background={"#EFEFED"}
        justifyContent={"center"}
        alignItems={"center"}
      >
        {/* 확장자와 무관하게 같은 아이콘을 쓴다. */}
        <CustomIcon
          src={"document-text"}
          width={16}
          height={16}
          color={"#6F6F6C"}
        />
      </Flex>
      <Flex flexDirection={"column"} gap={"2px"} flexGrow={"1"} minWidth={"0"}>
        <Flex flexDirection={"row"} alignItems={"center"} gap={"8px"}>
          <Text
            textStyle={"body-md-bold"}
            color={"#1D1D1B"}
            overflow={"hidden"}
            textOverflow={"ellipsis"}
            whiteSpace={"nowrap"}
          >
            {fileName}
          </Text>
          {status === "PARSING" && (
            <>
              <StatusBadge status={"자동 인식 중"} colorVariant={"blue"} />
              <Spinner size={"sm"} color={"#8F8F8C"} />
            </>
          )}
          {status === "UNSAVED" && (
            <StatusBadge status={"저장 전"} colorVariant={"yellow"} />
          )}
          {status === "SAVED" && (
            <StatusBadge status={"등록 완료"} colorVariant={"green"} />
          )}
        </Flex>
        {status === "SAVED" && registeredAt && (
          <Text textStyle={"body-sm-regular"} color={"#8F8F8C"}>
            {`등록일 ${format(registeredAt, "yyyy. MM. dd.")}`}
          </Text>
        )}
      </Flex>
      {isViewable && (
        <Flex flexShrink={"0"}>
          <AppButton variant={"white"} size={"small"} onClick={onViewFile}>
            파일 보기
          </AppButton>
        </Flex>
      )}
    </Flex>
  );
}
